import { Injectable, NgModule } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Resolve,
  Router,
  RouterModule,
  Routes,
} from "@angular/router";
import { SessionService } from "../session.service";
import { PermissionService } from "../permission.service";
import { map, take } from "rxjs/operators";
import { RolesListComponent } from "./roles-list/roles-list.component";
import { RoleComponent } from "./role/role.component";
import { AccountAdminGuard, LoggedInGuard } from "../auth/auth.guards";
import { from, Observable } from "rxjs";

@Injectable({
  providedIn: "root",
})
class ListGuard implements CanActivate {
  constructor(
    private PermissionService: PermissionService,
    private router: Router,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    return from(this.PermissionService.categories.roles.hasList()).pipe(
      map((res) => {
        if (!res) {
          this.router.navigate([""]);
        }
        return res;
      }),
    );
  }
}

@Injectable({
  providedIn: "root",
})
class WriteGuard implements CanActivate {
  constructor(
    private PermissionService: PermissionService,
    private router: Router,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    return from(this.PermissionService.categories.roles.hasWrite()).pipe(
      map((res) => {
        if (!res) {
          this.router.navigate([""]);
        }
        return res;
      }),
    );
  }
}

@Injectable({
  providedIn: "root",
})
class DetailGuard implements CanActivate {
  constructor(
    private PermissionService: PermissionService,
    private router: Router,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    return from(this.PermissionService.categories.roles.hasDetail()).pipe(
      map((res) => {
        if (!res) {
          this.router.navigate([""]);
        }
        return res;
      }),
    );
  }
}

@Injectable({
  providedIn: "root",
})
class PermissionsListGuard implements CanActivate {
  constructor(
    private PermissionService: PermissionService,
    private router: Router,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    return from(this.PermissionService.categories.roles.hasList()).pipe(
      map((res) => {
        if (!res) {
          this.router.navigate([""]);
        }
        return res;
      }),
    );
  }
}

@Injectable({ providedIn: "root" })
export class RolesResolve implements Resolve<any> {
  constructor(
    private PermissionService: PermissionService,
    private SessionService: SessionService,
  ) {}

  resolve() {
    return this.PermissionService.getRoles(
      this.SessionService.currentAccountValue!.accountID,
      true,
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class CopyRoleResolve implements Resolve<any> {
  constructor(
    private PermissionService: PermissionService,
    private router: Router,
    private SessionService: SessionService,
  ) {}

  resolve() {
    const roleID =
      this.router.getCurrentNavigation()?.extras.state?.copyRole?.id;
    if (roleID) {
      return this.PermissionService.getRole(
        this.SessionService.currentAccountValue!.accountID,
        roleID,
      ).pipe(take(1));
    }

    return undefined;
  }
}

@Injectable({ providedIn: "root" })
class PermissionsResolve implements Resolve<any> {
  constructor(
    private PermissionService: PermissionService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    return this.PermissionService.getPermissions(
      this.SessionService.currentAccountValue!.accountID,
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class RoleResolve implements Resolve<any> {
  constructor(
    private PermissionService: PermissionService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    return this.PermissionService.getRole(
      this.SessionService.currentAccountValue!.accountID,
      route.params.roleID,
    ).pipe(take(1));
  }
}

const routes: Routes = [
  {
    path: "",
    resolve: { roles: RolesResolve },
    canActivate: [LoggedInGuard],
    runGuardsAndResolvers: "always",
    data: { sidebar: "admin" },
    children: [
      {
        path: "list",
        component: RolesListComponent,
        canActivate: [AccountAdminGuard, ListGuard],
        data: { title: "Roles" },
      },
      {
        path: "create",
        component: RoleComponent,
        data: { title: "Create New Role" },
        canActivate: [WriteGuard, PermissionsListGuard],
        resolve: {
          permissions: PermissionsResolve,
          copyRole: CopyRoleResolve,
        },
      },
      {
        path: ":roleID",
        component: RoleComponent,
        canActivate: [WriteGuard, DetailGuard, PermissionsListGuard],
        data: { title: "Role" },
        runGuardsAndResolvers: "paramsChange",
        resolve: { role: RoleResolve, permissions: PermissionsResolve },
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class RolesRoutingModule {}

const rolesModuleLazyLoadRoutes: Routes = [
  {
    path: "roles",
    loadChildren: () => import("./roles.module").then((m) => m.RolesModule),
  },
];

export { rolesModuleLazyLoadRoutes };
